/* eslint-disable camelcase */
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { isApiError } from '@/api'
import { BulkInviteResponse } from '@/api/generated'
import { ResendInvitationResult } from '@/api/members'
import { useToast } from '@/components/Toast/ToastContext'
import { attachTeamId } from '@/pages/app/Team/inviteSectionUtils'
import { useNewMemberEligibility } from '@/pages/app/Team/useNewMemberEligibility'
import { AppDispatch } from '@/store'
import { approveJoinRequestThunk } from '@/store/features/joinRequest/joinRequestSlice'
import {
  bulkInviteUsersThunk,
  contactAdminThunk,
  fetchAllUsersThunk,
  inviteUserThunk,
  resendInvitationsThunk,
} from '@/store/features/member/memberSlice'
import { selectIsAdmin } from '@/store/features/user/userSlice'
import { ProductSeatType, Role } from '@/types'

export type ActionType = 'invite' | 'approveRequest'

export type InviteInput = { email: string; role?: Role; intended_seat_type?: ProductSeatType }

export type TeamMemberActionDialog =
  | {
      type: 'teamNeedsBillingInfo'
      email: string
      role: Role
      actionType: ActionType
      requestId?: string
      teamId?: string | null
      seatType?: ProductSeatType
    }
  | {
      type: 'willBecomePayer'
      email: string
      role: Role
      actionType: ActionType
      requestId?: string
      teamId?: string | null
      seatType?: ProductSeatType
    }
  | {
      type: 'warnEnterprise'
      email: string
      role: Role
      actionType: ActionType
      requestId?: string
      teamId?: string | null
      seatType?: ProductSeatType
    }
  | null

interface UseTeamMemberActionsOptions {
  onSuccess?: (email: string, actionType: ActionType) => void
  onError?: (error: { message: string; email: string; actionType: ActionType }) => void
}

/**
 * Centralized hook for all team member actions (inviting, approving join requests).
 * Handles eligibility checks, dialog management, and execution of actions.
 *
 * Usage:
 * ```tsx
 * const { inviteMember, inviteMembers, approveJoinRequest, isLoading, activeDialog, closeDialog, executeAction } = useTeamMemberActions({
 *   onSuccess: (email) => console.log(`Added ${email}`),
 * })
 *
 * // Render dialogs
 * <TeamMemberActionDialogs
 *   activeDialog={activeDialog}
 *   onClose={closeDialog}
 *   onExecute={executeAction}
 * />
 *
 * // Use actions
 * <Button onClick={() => inviteMember('user@example.com', 'member')} />
 * <Button onClick={() => inviteMembers([
 *   { email: 'user1@example.com', role: 'admin' },
 *   { email: 'user2@example.com', role: 'member' }
 * ])} />
 * <Button onClick={() => approveJoinRequest('request-id', 'user@example.com')} />
 * ```
 */
export const useTeamMemberActions = (options?: UseTeamMemberActionsOptions) => {
  const dispatch = useDispatch<AppDispatch>()
  const isAdmin = useSelector(selectIsAdmin)
  const [isLoading, setIsLoading] = useState(false)
  const [activeDialog, setActiveDialog] = useState<TeamMemberActionDialog>(null)
  const { updateToast } = useToast()
  const { getNewMemberEligibility } = useNewMemberEligibility()

  const messageFromError = (error: unknown): string => {
    // Thunks in this codebase reject with plain strings via rejectWithValue.
    if (typeof error === 'string') return error
    if (isApiError(error)) return error.message
    if (error instanceof Error) return error.message
    return 'An unexpected error occurred'
  }

  /**
   * Check eligibility and return the appropriate dialog type if needed
   */
  const checkEligibilityAndGetDialog = async (
    email: string,
    role: Role,
    actionType: ActionType,
    requestId?: string,
    teamId?: string | null,
    seatType?: ProductSeatType
  ): Promise<TeamMemberActionDialog> => {
    const eligibility = await getNewMemberEligibility(email)

    if (eligibility === 'teamNeedsBillingInfo') {
      return { type: 'teamNeedsBillingInfo', email, role, actionType, requestId, teamId, seatType }
    }
    if (eligibility === 'willBecomePayer') {
      return { type: 'willBecomePayer', email, role, actionType, requestId, teamId, seatType }
    }
    if (eligibility === 'warnEnterprise') {
      return { type: 'warnEnterprise', email, role, actionType, requestId, teamId, seatType }
    }
    if (eligibility === 'alreadyInEnterprise') {
      throw new Error('User is already in an enterprise')
    }

    return null
  }

  /**
   * Execute the actual invite action
   */
  const executeInvite = async (email: string, role: Role, teamId?: string | null, seatType?: ProductSeatType) => {
    if (isAdmin) {
      const [request] = attachTeamId([{ email, role, intended_seat_type: seatType }], teamId)
      await dispatch(inviteUserThunk(request)).unwrap()
      updateToast(`Invited ${email}`, 'success')
    } else {
      await dispatch(
        contactAdminThunk({
          invite_email: email,
          message: 'Please add this user to our Wispr Flow team',
        })
      ).unwrap()
      updateToast('Successfully sent message to admins', 'success')
    }
    options?.onSuccess?.(email, 'invite')
  }

  /**
   * Execute bulk invite action
   */
  const executeBulkInvite = async (invites: InviteInput[], teamId?: string | null) => {
    // No summary toast here: bulk-result presentation is caller-specific
    // (Team renders a results panel; GetStarted toasts) — callers read the
    // returned BulkInviteResponse.
    const result = await dispatch(bulkInviteUsersThunk({ invites: attachTeamId(invites, teamId) })).unwrap()

    result.successful.forEach((invite) => {
      options?.onSuccess?.(invite.email, 'invite')
    })

    return result
  }

  /**
   * Execute the actual approve action
   */
  const executeApproval = async (requestId: string, email: string) => {
    await dispatch(approveJoinRequestThunk(requestId)).unwrap()
    updateToast('Request approved', 'success')
    options?.onSuccess?.(email, 'approveRequest')
  }

  /**
   * Invite a user to the team (with eligibility check)
   */
  const inviteMember = async (
    email: string,
    role: Role = 'member',
    skipEligibilityCheck: boolean = false,
    teamId?: string | null,
    seatType?: ProductSeatType
  ) => {
    if (isLoading) return
    try {
      setIsLoading(true)

      if (!skipEligibilityCheck) {
        const dialog = await checkEligibilityAndGetDialog(email, role, 'invite', undefined, teamId, seatType)
        if (dialog) {
          setActiveDialog(dialog)
          return
        }
      }

      await executeInvite(email, role, teamId, seatType)
    } catch (error) {
      const message = messageFromError(error)
      updateToast(message, 'error')
      options?.onError?.({ message, email, actionType: 'invite' })
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Approve a join request (with eligibility check)
   */
  const approveJoinRequest = async (
    requestId: string,
    email: string,
    role: Role = 'member',
    skipEligibilityCheck: boolean = false
  ) => {
    if (isLoading) return

    try {
      setIsLoading(true)

      if (!skipEligibilityCheck && isAdmin) {
        const dialog = await checkEligibilityAndGetDialog(email, role, 'approveRequest', requestId)
        if (dialog) {
          setActiveDialog(dialog)
          return
        }
      }

      await executeApproval(requestId, email)
    } catch (error) {
      const message = messageFromError(error)
      updateToast(message, 'error')
      options?.onError?.({ message, email, actionType: 'approveRequest' })
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Execute the action that was deferred due to a dialog
   * Used when user confirms they want to proceed after seeing a dialog
   */
  const executeAction = async (skipEligibilityCheck: boolean = true) => {
    if (!activeDialog || isLoading) return

    const { email, role, actionType, requestId, teamId, seatType } = activeDialog
    setActiveDialog(null) // Close dialog before executing

    if (actionType === 'invite') {
      await inviteMember(email, role, skipEligibilityCheck, teamId, seatType)
    } else if (actionType === 'approveRequest' && requestId) {
      await approveJoinRequest(requestId, email, role, skipEligibilityCheck)
    }
  }

  /**
   * Invite multiple members (uses bulk invite for multiple, single invite for one)
   * This is the smart function that chooses between single and bulk invite
   */
  const inviteMembers = async (
    invites: InviteInput[],
    skipEligibilityCheck: boolean = false,
    teamId?: string | null
  ): Promise<BulkInviteResponse | 'error' | undefined> => {
    if (isLoading) return
    try {
      setIsLoading(true)

      // Normalize invites to ensure all have role
      const normalizedInvites: Array<InviteInput & { role: Role }> = invites.map((invite) => ({
        email: invite.email,
        role: invite.role || ('member' as Role),
        intended_seat_type: invite.intended_seat_type,
      }))

      // Use single invite for one user, bulk invite for multiple
      if (normalizedInvites.length === 1) {
        const { email, role, intended_seat_type: seatType } = normalizedInvites[0]
        await inviteMember(email, role, skipEligibilityCheck, teamId, seatType)
      } else if (normalizedInvites.length > 1) {
        if (!isAdmin) {
          // The bulk endpoint is admin-only; non-admins get the same
          // contact-admin fallback executeInvite uses for a single email.
          const failedEmails: string[] = []
          for (const { email } of normalizedInvites) {
            try {
              await dispatch(
                contactAdminThunk({
                  invite_email: email,
                  message: 'Please add this user to our Wispr Flow team',
                })
              ).unwrap()
              options?.onSuccess?.(email, 'invite')
            } catch {
              failedEmails.push(email)
            }
          }
          if (failedEmails.length === normalizedInvites.length) {
            updateToast('Failed to send message to admins', 'error')
            return 'error'
          }
          if (failedEmails.length > 0) {
            updateToast(`Sent message to admins, but it failed for ${failedEmails.join(', ')}`, 'error')
          } else {
            updateToast('Successfully sent message to admins', 'success')
          }
          return
        }
        return await executeBulkInvite(normalizedInvites, teamId)
      }
    } catch (error) {
      const message = messageFromError(error)
      updateToast(message, 'error')
      options?.onError?.({ message, email: 'multiple', actionType: 'invite' })
      // The batch failed atomically (e.g. seat cap): nothing was sent, so tell
      // the caller to keep its input intact rather than clear it.
      return 'error'
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Resend pending/error invites. Doesn't touch `isLoading`: per-invite progress lives in the slice so
   * the invite form stays usable. Outcome feedback is caller-specific (row toast vs bulk panel), so
   * only a whole-call failure toasts here; it resolves to `undefined` in that case.
   */
  const resendInvites = async (invitationIds: string[]): Promise<ResendInvitationResult[] | undefined> => {
    try {
      const results = await dispatch(resendInvitationsThunk({ invitationIds })).unwrap()
      // A resent error row is pending again; refetch rather than patching rows client-side.
      void dispatch(fetchAllUsersThunk())
      return results
    } catch (error) {
      updateToast(messageFromError(error), 'error')
      return undefined
    }
  }

  /**
   * Close the active dialog without executing the action
   */
  const closeDialog = () => {
    setActiveDialog(null)
  }

  return {
    inviteMember,
    inviteMembers,
    approveJoinRequest,
    resendInvites,
    isLoading,
    activeDialog,
    closeDialog,
    executeAction,
  }
}
